import type { Editor } from '@tiptap/core';
import type { EditorState as ProseMirrorEditorState } from '@tiptap/pm/state';
import type { IconType } from 'react-icons';

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import {
  FiBold,
  FiCode,
  FiHash,
  FiItalic,
  FiList,
  FiMessageSquare,
  FiMinus,
} from 'react-icons/fi';

export interface MessageMarkdownSlashCommandRange {
  from: number;
  to: number;
}

export interface MessageMarkdownSlashCommand {
  id: string;
  label: string;
  aliases: string[];
  icon: IconType;
  run: () => void;
}

export interface MessageMarkdownSlashMenuState {
  active: boolean;
  query: string;
  range: MessageMarkdownSlashCommandRange | null;
  selectedIndex: number;
}

interface MessageMarkdownSlashTriggerMatch {
  from: number;
  query: string;
}

interface MessageMarkdownSlashCommandDefinition {
  id: string;
  label: string;
  aliases: string[];
  icon: IconType;
  run: (editor: Editor) => void;
}

type MessageMarkdownSlashMenuMeta =
  | { type: 'close' }
  | { type: 'setSelectedIndex'; index: number };

export const MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY =
  new PluginKey<MessageMarkdownSlashMenuState>('messageMarkdownSlashMenu');

export const INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE: MessageMarkdownSlashMenuState =
  {
    active: false,
    query: '',
    range: null,
    selectedIndex: 0,
  };

export function createMessageMarkdownSlashCommands(
  editor: Editor,
): MessageMarkdownSlashCommand[] {
  const commands: MessageMarkdownSlashCommandDefinition[] = [
    {
      id: 'bold',
      label: 'Bold',
      aliases: ['strong'],
      icon: FiBold,
      run: (currentEditor) => currentEditor.chain().focus().toggleBold().run(),
    },
    {
      id: 'italic',
      label: 'Italic',
      aliases: ['emphasis', 'em'],
      icon: FiItalic,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleItalic().run(),
    },
    {
      id: 'strike',
      label: 'Strike',
      aliases: ['strikethrough', 'delete'],
      icon: FiMinus,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleStrike().run(),
    },
    {
      id: 'bullet-list',
      label: 'Bullet List',
      aliases: ['bullets', 'ul', 'unordered list'],
      icon: FiList,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleBulletList().run(),
    },
    {
      id: 'ordered-list',
      label: 'Ordered List',
      aliases: ['numbered list', 'ol'],
      icon: FiHash,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleOrderedList().run(),
    },
    {
      id: 'blockquote',
      label: 'Quote',
      aliases: ['blockquote'],
      icon: FiMessageSquare,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleBlockquote().run(),
    },
    {
      id: 'code-block',
      label: 'Code',
      aliases: ['code block', 'pre'],
      icon: FiCode,
      run: (currentEditor) =>
        currentEditor.chain().focus().toggleCodeBlock().run(),
    },
  ];

  return commands.map((command) => ({
    ...command,
    run: () => command.run(editor),
  }));
}

export function filterMessageMarkdownSlashCommands(
  commands: MessageMarkdownSlashCommand[],
  query: string,
): MessageMarkdownSlashCommand[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return commands;
  }

  return commands.filter((command) => {
    const haystacks = [command.label, ...command.aliases];
    return haystacks.some((value) =>
      value.toLowerCase().includes(normalizedQuery),
    );
  });
}

export function findMessageMarkdownSlashTrigger(
  textBeforeCursor: string,
): MessageMarkdownSlashTriggerMatch | null {
  const match = /(?:^|\s)\/([^\s/]*)$/.exec(textBeforeCursor);
  if (!match) {
    return null;
  }

  const slashOffset = match.index + match[0].lastIndexOf('/');
  return {
    from: slashOffset,
    query: match[1] ?? '',
  };
}

function isCodeContext(state: ProseMirrorEditorState): boolean {
  const marks = [
    ...state.selection.$from.marks(),
    ...(state.storedMarks ?? []),
  ];
  return (
    state.selection.$from.parent.type.spec.code === true ||
    marks.some((mark) => mark.type.spec.code === true)
  );
}

function getMessageMarkdownSlashMenuMatch(
  state: ProseMirrorEditorState,
): { query: string; range: MessageMarkdownSlashCommandRange } | null {
  if (!state.selection.empty) {
    return null;
  }

  const { $from, from } = state.selection;
  if (!$from.parent.isTextblock || isCodeContext(state)) {
    return null;
  }

  const textBeforeCursor = $from.parent.textBetween(
    0,
    $from.parentOffset,
    '\0',
    '\0',
  );
  const trigger = findMessageMarkdownSlashTrigger(textBeforeCursor);
  if (!trigger) {
    return null;
  }

  return {
    query: trigger.query,
    range: {
      from: from - (textBeforeCursor.length - trigger.from),
      to: from,
    },
  };
}

function clampSelectedIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }

  return Math.min(Math.max(index, 0), length - 1);
}

export function closeMessageMarkdownSlashMenu(editor: Editor) {
  editor.view.dispatch(
    editor.state.tr.setMeta(MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY, {
      type: 'close',
    } satisfies MessageMarkdownSlashMenuMeta),
  );
}

export function setMessageMarkdownSlashMenuSelectedIndex(
  editor: Editor,
  index: number,
) {
  editor.view.dispatch(
    editor.state.tr.setMeta(MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY, {
      type: 'setSelectedIndex',
      index,
    } satisfies MessageMarkdownSlashMenuMeta),
  );
}

export function runMessageMarkdownSlashCommand(
  editor: Editor,
  command: MessageMarkdownSlashCommand,
  range: MessageMarkdownSlashCommandRange,
) {
  editor.chain().focus().deleteRange(range).run();
  command.run();
}

export const MessageMarkdownSlashMenuExtension = Extension.create({
  name: 'messageMarkdownSlashMenu',

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin<MessageMarkdownSlashMenuState>({
        key: MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY,
        state: {
          init: () => INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE,
          apply: (transaction, pluginState, _oldState, newState) => {
            const meta = transaction.getMeta(
              MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY,
            ) as MessageMarkdownSlashMenuMeta | undefined;

            if (meta?.type === 'close') {
              return INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE;
            }

            const match = getMessageMarkdownSlashMenuMatch(newState);
            if (!match) {
              return INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE;
            }

            const commands = filterMessageMarkdownSlashCommands(
              createMessageMarkdownSlashCommands(editor),
              match.query,
            );
            if (commands.length === 0) {
              return INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE;
            }

            const requestedIndex =
              meta?.type === 'setSelectedIndex'
                ? meta.index
                : pluginState.selectedIndex;
            const selectedIndex =
              pluginState.active &&
              pluginState.query === match.query &&
              pluginState.range?.from === match.range.from
                ? clampSelectedIndex(requestedIndex, commands.length)
                : 0;

            return {
              active: true,
              query: match.query,
              range: match.range,
              selectedIndex,
            };
          },
        },
        props: {
          handleKeyDown(view, event) {
            const menuState =
              MESSAGE_MARKDOWN_SLASH_MENU_PLUGIN_KEY.getState(view.state) ??
              INACTIVE_MESSAGE_MARKDOWN_SLASH_MENU_STATE;
            if (!menuState.active || !menuState.range) {
              return false;
            }

            const commands = filterMessageMarkdownSlashCommands(
              createMessageMarkdownSlashCommands(editor),
              menuState.query,
            );
            if (commands.length === 0) {
              return false;
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setMessageMarkdownSlashMenuSelectedIndex(
                editor,
                (menuState.selectedIndex + 1) % commands.length,
              );
              return true;
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setMessageMarkdownSlashMenuSelectedIndex(
                editor,
                (menuState.selectedIndex - 1 + commands.length) %
                  commands.length,
              );
              return true;
            }

            if (event.key === 'Enter') {
              event.preventDefault();
              runMessageMarkdownSlashCommand(
                editor,
                commands[menuState.selectedIndex] ?? commands[0],
                menuState.range,
              );
              return true;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              closeMessageMarkdownSlashMenu(editor);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
